import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { RollingNetDepositLimitItemState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimitItem';
import {
    RollingDepositLimitForm,
    NoteSection,
    SignupDepositLimitsEditableCardFrame,
    SignupDepositLimitsPanelRoot,
    SignupDepositLimitsMainTitle,
    SignupDepositLimitsSubtitle,
    SignupReadonlyCardPeriod,
    SignupDepositLimitsLoadingText,
    SignupDepositLimitAmountShell,
    SignupDepositLimitCurrencyPrefix,
    SignupDepositLimitNativeInput,
    SignupDepositLimitAmountLabelHidden,
    SignupCalculationTypeRadioGroup,
    SignupCalculationTypeRadioCard,
    SignupCalculationTypeRadioCaption,
    SignupCalculationTypeRadioIndicator,
    SignupDepositLimitsLoadingArea,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimit.style';
import { SpinnerLight } from 'src/domains/layouts/webview/components/loaders/loadingSpinner/LoadingSpinner.style';
import {
    SignupRollingNetDepositLimitState,
    InfoMessageType,
    SignupRollingDepositLimitWindowDaysType,
    SignupDepositLimitsType,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimit.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SignupLimitsDescription } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsCopy';
import {
    ReadonlyCardsStack,
    FormFieldBlock,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.style';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { assertNever } from 'src_common/common/assertNever';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { DateTime } from 'src_common/utils/time/time';
import { getRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { shouldUseCalendarPeriodLabels } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/depositLimitType';

export const renderMessage = (infoMessage: InfoMessageType | null): JSX.Element | string => {
    if (infoMessage === 'error') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits-error-message'
                        defaultText='Sorry, we seem to have a problem. Please try again.'
                    />
                }
            />
        );
    } else if (infoMessage === 'success') {
        return (
            <Messages
                type='success'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits.success-message'
                        defaultText='Set limits finished successfully.'
                    />
                }
            />
        );
    } else if (infoMessage === 'empty') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits-error-message-empty-deposit'
                        defaultText='You have to choose at least one deposit limit to save.'
                    />
                }
            />
        );
    } else if (infoMessage === 'belowOne') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits-error-message-below-one-deposit'
                        defaultText='You cannot set deposit limit below 1'
                    />
                }
            />
        );
    } else if (infoMessage === 'amountTypeMismatch') {
        return (
            <Messages
                type='error'
                marginBottom='8px'
                message={
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits-error-message-amount-and-type'
                        defaultText='You have to choose a limit amount and type.'
                    />
                }
            />
        );
    }

    return '';
};

export const SignupDepositLimitsLoader = (): JSX.Element => (
    <SignupDepositLimitsLoadingArea data-test='signup-deposit-limits-loading'>
        <SpinnerLight type='basic' />
        <SignupDepositLimitsLoadingText>
            <I18n
                langKey='signup-journey.contact-preferences.loading'
                defaultText='Loading...'
            />
        </SignupDepositLimitsLoadingText>
    </SignupDepositLimitsLoadingArea>
);

function signupDepositLimitPeriodLabel(
    languagesState: LanguagesState,
    depositLimitTime: 'daily' | 'weekly' | 'monthly',
    useCalendarPeriodLabels: boolean
): string {
    if (useCalendarPeriodLabels) {
        if (depositLimitTime === 'daily') {
            return languagesState.getTranslation(
                'account.net-deposit-limit.label.calendarDaily',
                'Set New Daily Limit'
            );
        } else if (depositLimitTime === 'weekly') {
            return languagesState.getTranslation(
                'account.net-deposit-limit.label.calendarWeekly',
                'Set New Weekly Limit'
            );
        } else if (depositLimitTime === 'monthly') {
            return languagesState.getTranslation(
                'account.net-deposit-limit.label.calendarMonthly',
                'Set New Monthly Limit'
            );
        }
    } else {
        if (depositLimitTime === 'daily') {
            return languagesState.getTranslation('signup-journey.deposit-limits.label.one-day', 'Set New 1 Day Limit');
        } else if (depositLimitTime === 'weekly') {
            return languagesState.getTranslation(
                'signup-journey.deposit-limits.label.seven-days',
                'Set New 7 Days Limit'
            );
        } else if (depositLimitTime === 'monthly') {
            return languagesState.getTranslation(
                'signup-journey.deposit-limits.label.thirty-days',
                'Set New 30 Days Limit'
            );
        }
    }

    return assertNever('signupDepositLimitPeriodLabel', depositLimitTime);
}

function signupDepositLimitDataTestSuffix(
    limitsType: SignupDepositLimitsType,
    depositLimitTime: 'daily' | 'weekly' | 'monthly'
): string {
    if (limitsType === 'rolling' && depositLimitTime === 'daily') {
        return '1-day';
    }
    if (limitsType === 'rolling' && depositLimitTime === 'weekly') {
        return '7-day';
    }
    if (limitsType === 'rolling' && depositLimitTime === 'monthly') {
        return '30-day';
    }
    return depositLimitTime;
}

function depositLimitTimeToWindowDays(
    depositLimitTime: 'daily' | 'weekly' | 'monthly'
): SignupRollingDepositLimitWindowDaysType {
    if (depositLimitTime === 'daily') {
        return 1;
    }
    if (depositLimitTime === 'weekly') {
        return 7;
    }
    return 30;
}

const pendingMessageSignup = (
    configComponents: ReturnType<typeof ConfigComponents.get>,
    currency: CurrencyType,
    amount: number,
    lastUpdateDate: string
): JSX.Element | undefined => {
    const until = DateTime.from(lastUpdateDate)?.addDays(1).format('HH:mm DD-MM-YYYY');

    if (amount === 0 || until === undefined) {
        return undefined;
    }

    const next = configComponents.precision.newFromAnything(amount).format(currency);

    return (
        <I18n
            langKey='signup-journey.rolling-net-deposit-limits.pending-new-limit'
            defaultText='New limit of {next} will be available at {until}'
            params={{ next, until }}
        />
    );
};

const renderPendingSignup = (
    configComponents: ReturnType<typeof ConfigComponents.get>,
    currency: CurrencyType,
    limits: getRollingLimits.LimitsObjectSchemaType | undefined
): React.ReactElement | null => {
    const pendingLimitValue = limits?.pendingLimitValue;
    const pendingRequestedAt = limits?.pendingRequestedAt;

    if (
        pendingLimitValue === null ||
        pendingLimitValue === undefined ||
        pendingRequestedAt === null ||
        pendingRequestedAt === undefined
    ) {
        return null;
    }

    const message = pendingMessageSignup(configComponents, currency, pendingLimitValue, pendingRequestedAt);

    return (
        <Messages
            type='success'
            message={message}
            dataTest='limit-set-message'
        />
    );
};

export const SignupDepositLimitsEditableCard = observer(
    'SignupDepositLimitsEditableCard',
    ({
        limitsState,
        itemState,
        depositLimitTime,
    }: {
        limitsState: SignupRollingNetDepositLimitState;
        itemState: RollingNetDepositLimitItemState;
        depositLimitTime: 'daily' | 'weekly' | 'monthly';
    }) => {
        const common = useCommon();
        const configComponents = ConfigComponents.get(common);
        const languagesState = LanguagesState.get(common);
        const windowDays = depositLimitTimeToWindowDays(depositLimitTime);
        const limitType = limitsState.limitTypeForWindow(windowDays);
        const useCalendarLabels = shouldUseCalendarPeriodLabels(limitType);
        const periodForDataTest = signupDepositLimitDataTestSuffix(limitType, depositLimitTime);
        const limits = itemState.getLimits();

        const typeLabelId = `signup-deposit-limits-type-label-${periodForDataTest}`;
        const amountInputId = `signup-deposit-limit-amount-${periodForDataTest}`;
        const currencySymbol = BasicDataModel.get(common).moneySymbol;

        return (
            <SignupDepositLimitsEditableCardFrame data-test={`signup-deposit-limit-card-${periodForDataTest}`}>
                <SignupDepositLimitsPanelRoot>
                    <SignupReadonlyCardPeriod data-test={`signup-limit-period-${periodForDataTest}`}>
                        {signupDepositLimitPeriodLabel(languagesState, depositLimitTime, useCalendarLabels)}
                    </SignupReadonlyCardPeriod>
                    <FormFieldBlock isSignup={true}>
                        <SignupCalculationTypeRadioGroup
                            role='radiogroup'
                            aria-labelledby={typeLabelId}
                        >
                            <SignupCalculationTypeRadioCard
                                type='button'
                                role='radio'
                                aria-checked={limitsState.calculationTypeForWindow(windowDays) === 'gross'}
                                onClick={(): void => {
                                    limitsState.setCalculationTypeForWindow(windowDays, 'gross');
                                }}
                                data-test={`signup-deposit-limits-type-gross-${periodForDataTest}`}
                            >
                                <SignupCalculationTypeRadioCaption>
                                    <I18n
                                        langKey='account.deposit-limits-edit.type.gross'
                                        defaultText='Deposit Limits'
                                    />
                                </SignupCalculationTypeRadioCaption>
                                <SignupCalculationTypeRadioIndicator
                                    aria-hidden={true}
                                    isSelected={limitsState.calculationTypeForWindow(windowDays) === 'gross'}
                                />
                            </SignupCalculationTypeRadioCard>
                            <SignupCalculationTypeRadioCard
                                type='button'
                                role='radio'
                                aria-checked={limitsState.calculationTypeForWindow(windowDays) === 'net'}
                                onClick={(): void => {
                                    limitsState.setCalculationTypeForWindow(windowDays, 'net');
                                }}
                                data-test={`signup-deposit-limits-type-net-${periodForDataTest}`}
                            >
                                <SignupCalculationTypeRadioCaption>
                                    <I18n
                                        langKey='account.deposit-limits-edit.type.net'
                                        defaultText='Net Deposit Limits'
                                    />
                                </SignupCalculationTypeRadioCaption>
                                <SignupCalculationTypeRadioIndicator
                                    aria-hidden={true}
                                    isSelected={limitsState.calculationTypeForWindow(windowDays) === 'net'}
                                />
                            </SignupCalculationTypeRadioCard>
                        </SignupCalculationTypeRadioGroup>
                    </FormFieldBlock>
                    <SignupDepositLimitAmountLabelHidden htmlFor={amountInputId}>
                        <I18n
                            langKey='account.deposit-limits-edit.amount.label'
                            defaultText='Amount'
                        />
                    </SignupDepositLimitAmountLabelHidden>
                    <SignupDepositLimitAmountShell hasError={itemState.isError}>
                        <SignupDepositLimitCurrencyPrefix aria-hidden={true}>
                            {currencySymbol}
                        </SignupDepositLimitCurrencyPrefix>
                        <SignupDepositLimitNativeInput
                            id={amountInputId}
                            type='text'
                            inputMode='decimal'
                            maxLength={10}
                            ref={itemState.setAmountInputRef}
                            value={itemState.limitAmount.value}
                            placeholder='Not set'
                            onChange={itemState.onChangeInput}
                            onBlur={itemState.onBlur}
                            autoComplete='off'
                            data-test={`signup-deposit-limit-amount-${periodForDataTest}`}
                        />
                    </SignupDepositLimitAmountShell>
                    {renderPendingSignup(configComponents, limitsState.selectedCurrency, limits)}
                    {itemState.errorMaxLimitExceeded ? (
                        <Messages
                            type='error'
                            message={
                                <I18n
                                    langKey='signup-journey.rolling-net-deposit-limits.max-limit-input'
                                    defaultText='Please set limit below {maxLimitValue}'
                                    params={{
                                        maxLimitValue: itemState.formattedDepositLimitCeiling,
                                    }}
                                />
                            }
                            dataTest={`signup-limit-error-${periodForDataTest}`}
                        />
                    ) : null}
                </SignupDepositLimitsPanelRoot>
            </SignupDepositLimitsEditableCardFrame>
        );
    }
);

interface PropsType {
    state: SignupRollingNetDepositLimitState;
}

export const SignupDepositLimit = observer('SignupDepositLimit', ({ state }: PropsType) => {
    const common = useCommon();
    const {
        config: { signupAdditionalNoteOnLimits },
    } = ConfigComponents.get(common);

    if (state.isLoading) {
        return <SignupDepositLimitsLoader />;
    }

    return (
        <RollingDepositLimitForm>
            <SignupDepositLimitsMainTitle data-test='signup-deposit-limits-main-title'>
                <I18n
                    langKey='account.deposit-limit-readonly.title'
                    defaultText='Deposit Limits'
                />
            </SignupDepositLimitsMainTitle>
            <SignupDepositLimitsSubtitle data-test='signup-deposit-limits-subtitle'>
                <I18n
                    langKey='account.deposit-limit-readonly.subtitle'
                    defaultText='Set individual limits for each time period.'
                />
            </SignupDepositLimitsSubtitle>
            <SignupLimitsDescription dataTest='rolling-net-deposit-limits-content' />
            {signupAdditionalNoteOnLimits ? (
                <NoteSection data-test='rolling-net-deposit-limits-note'>
                    <I18n
                        langKey='signup-journey.rolling-net-deposit-limits-calendar.responsible-gambling.rolling-note'
                        defaultText='Note, if you set no limits, then the cap amounts indicated will be applied as your limit.'
                    />
                </NoteSection>
            ) : null}
            <ReadonlyCardsStack>
                <SignupDepositLimitsEditableCard
                    limitsState={state}
                    itemState={state.daily}
                    depositLimitTime='daily'
                />
                <SignupDepositLimitsEditableCard
                    limitsState={state}
                    itemState={state.weekly}
                    depositLimitTime='weekly'
                />
                <SignupDepositLimitsEditableCard
                    limitsState={state}
                    itemState={state.monthly}
                    depositLimitTime='monthly'
                />
            </ReadonlyCardsStack>
            {renderMessage(state.infoMessage)}
        </RollingDepositLimitForm>
    );
});
